import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { v1Backend } from 'services/api/client';
import PageLoader from './LoadingButton/PageLoader';
import { STATUS_TO_PATH } from 'utils/onboardingRoute';

/**
 * Hard-gates onboarding step pages to the company's actual server-side step.
 *
 * The onboarding routes (/gst, /bank, /terms, /pricing, /under_review) are
 * otherwise directly reachable, so a user stuck at e.g. BANK_DETAILS could open
 * /terms, sign the document, and then have `PUT /step` rejected with
 * "PAN number is required" because the backend switch is still on the
 * BANK_DETAILS case. This guard redirects any onboarding page that does not
 * match the company's current step to the page that does.
 *
 * Status is fetched fresh on every mount (staleTime/cacheTime 0) so a legit
 * forward transition (e.g. T&C sign -> status MEMBERSHIP -> /pricing) is never
 * bounced back to the previous step by a stale cached status. The shared
 * ['companydetails'] query used by the progress bar stays cached for 5 min and
 * is intentionally NOT reused here for that reason.
 */
const OnboardingStepGuard = ({ children }) => {
  const location = useLocation();

  const { data, isLoading, isError, error } = useQuery(
    ['onboarding-step-guard'],
    async () => {
      const res = await v1Backend.get('onboard-company');
      return res?.data ?? null;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  if (isLoading) return <PageLoader />;

  // SECURITY (VAPT rescan PT-34 — unauthenticated access to onboarding step URLs):
  // These pages must never render without a valid session. The `onboard-company`
  // middleware is SOFT (non-rejecting): an unauthenticated visitor (e.g. incognito
  // hitting /gst or /terms) gets EITHER a 401 OR a 200 with an empty body. Treat
  // both as "no session" and redirect to login. An authenticated company always
  // resolves to a document with _id / companyOnboardingStatus.
  const authStatus = error?.response?.status;
  const hasCompanySession = Boolean(
    data && (data._id || data.companyOnboardingStatus)
  );
  if ((isError && (authStatus === 401 || authStatus === 403)) || !hasCompanySession) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated onboarding company: hard-gate to the current server-side step.
  const status = data?.companyOnboardingStatus;
  const canonicalPath = status ? STATUS_TO_PATH[status] : undefined;
  // Known session but unmapped status — let the page's own handling take over.
  if (!canonicalPath) return children;

  // Already on the correct page for the current step.
  if (location.pathname.startsWith(canonicalPath)) return children;

  return <Navigate to={canonicalPath} replace />;
};

export default OnboardingStepGuard;
